import React, { ReactNode } from "react";

export interface MessageManager {
  show(
    text: string | ReactNode,
    type?: MessageType,
    delay?: number,
    hideCloseButton?: boolean,
    style?: React.CSSProperties,
    onClose?: () => void
  ): Message;
  showInfo(text: string, delay: number, hideCloseButton: boolean): Message;
  showWarning(text: string, delay: number, hideCloseButton: boolean): Message;
  showError(text: string, delay: number, hideCloseButton: boolean): Message;
  changeStateClassById(id: number, newClass?: string): void;
  removeMessageById(id: number): void;
  state: MessagesState;
  setHidden(hidden: boolean): void;
}

export interface MessagesState {
  messages: Message[];
  hidden: boolean;
}

export interface MessagesProps {
  managerObject: { messages: {} };
}

/**
 * Eine Klasse für Toasts
 */
export default class Messages
  extends React.Component<MessagesProps, MessagesState>
  implements MessageManager
{
  currentId: number;

  constructor(props: MessagesProps) {
    super(props);
    this.currentId = 0;
    const hidden = localStorage.getItem("messagesHidden") === "true";
    this.state = { messages: [], hidden };
    if (props.managerObject) props.managerObject.messages = this;
  }

  componentDidUpdate(prevProps: Readonly<MessagesProps>, prevState: Readonly<MessagesState>): void {
    if (
      prevState.hidden !== this.state.hidden ||
      prevState.messages.length !== this.state.messages.length
    ) {
      APP.trigger("messagesChanged");
    }
    if (prevState.hidden !== this.state.hidden) {
      APP.dirty();
    }
  }

  setHidden(hidden: boolean) {
    const wasHidden = this.state.hidden;
    localStorage.setItem("messagesHidden", String(hidden));
    this.setState({ hidden });

    // Timer aller Messages anpassen wenn sich der hidden-Zustand ändert
    if (wasHidden !== hidden) {
      for (const message of this.state.messages) {
        if (message.delay > 0 && message.removeTimerId != null) {
          clearTimeout(message.removeTimerId);
          const elapsed = Date.now() - message.createdAt;
          const normalRemaining = Math.max(0, message.delay - elapsed);
          // hidden → visible: mindestens die volle Original-Dauer nochmal anzeigen
          // visible → hidden: 10x Restzeit, mindestens 60s
          const newDelay = hidden
            ? Math.max(normalRemaining * 10, 60000)
            : Math.max(normalRemaining, message.delay);
          if (newDelay <= 0) {
            this.removeMessageById(message.id);
          } else {
            message.removeTimerId = setTimeout(() => {
              this.removeMessageById(message.id);
            }, newDelay);
          }
        }
      }
    }
  }

  render() {
    if (this.state.messages.length === 0 || this.state.hidden) {
      return null;
    }

    return (
      <div id="messageContainer" className="message-frame">
        <div className="assistant-avatar icon"></div>
        <div className="message-header">
          <button
            className="minimize-button"
            onClick={() => this.setHidden(true)}
            aria-label={I18N("aria::Nachrichten ausblenden")}
          >
            {I18N("Ausblenden")}
          </button>
        </div>
        <div className="message-list">
          {this.state.messages.map((message) => (
            <div
              key={"messagesKey" + message.id}
              className={`message ${message.type} ${message.stateClass ? message.stateClass : ""}`}
              style={message.style}
            >
              <span className="text" dangerouslySetInnerHTML={{ __html: message.text }}></span>
              {message.count > 1 && <span className="count">{message.count}x</span>}
              {!message.hideCloseButton && (
                <span className="closeButton" onClick={() => this.removeMessageById(message.id)}>
                  ✖
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * Zeigt eine Nachricht an. Kann komplett spezifiziert werden
   * @param text Welche Nachricht?
   * @param type Welcher MessageType? Info, Warning oder Error
   * @param delay Wie lange soll die Nachricht stehen bleiben (in ms), 0 = unendlich
   * @param hideCloseButton Soll das x zum schließen nicht angezeigt werden?
   * @param style Individuelle CSS-Styles für die Nachricht
   * @param onClose Callback, wird beim Timeout oder Close-Klick der Message aufgerufen
   * @returns Message-Objekt mit Funktionen hide und isVisible.
   */
  show(
    text: string | React.ReactNode,
    type: MessageType = "info",
    delay: number = 12000,
    hideCloseButton: boolean = false,
    style?: React.CSSProperties,
    onClose?: () => void
  ) {
    if (APP.runningTests) {
      const textString = typeof text === "string" ? text : String(text); // Basic handling for tests
      const message = APP.logMessagesForTests.find((m) => m.text === textString);
      if (message) {
        message.count++;
      } else {
        APP.logMessagesForTests.push({
          text: textString,
          type,
          count: 1,
          onClose: () => {
            APP.logMessagesForTests = APP.logMessagesForTests.filter((m) => m.text !== textString);
            onClose && onClose();
          },
        });
      }
      return;
    }

    const id = this.currentId++;
    const stateClass = "coming";
    const newMessage = new Message(
      this,
      id,
      text as string, // Treat as string internally for now based on old signature, or update Message class if needed. We assume dangerouslySetInnerHTML handles it if string, but ReactNode needs direct render if we change Message class. Looking at existing code, `typeof text === 'string'` was assumed elsewhere.
      type,
      hideCloseButton,
      stateClass,
      style,
      onClose
    );

    let wasDuplicate = false;

    // delay ist wichtig, damit es nicht zu update3d-Recursion kommt
    _.delay(() => {
      this.setState(
        (prevState) => {
          const duplicate = prevState.messages.find((message) => message.equals(newMessage));
          if (duplicate) {
            duplicate.addCount();
            wasDuplicate = true;
            // Klonen des Arrays erzwingt ein Re‑Render
            return { messages: [...prevState.messages] };
          } else {
            return { messages: prevState.messages.concat(newMessage) };
          }
        },
        () => {
          // Bestimme, welche Message animiert werden soll:
          // Wenn es ein Duplikat war, holen wir das existierende Message-Objekt, ansonsten newMessage.
          const messageToAnimate = wasDuplicate
            ? this.state.messages.find((m) => m.equals(newMessage))
            : newMessage;

          // Falls die Message gefunden wird und noch im "coming"-Zustand ist, starte die Animationen.
          if (messageToAnimate && messageToAnimate.stateClass === "coming") {
            setTimeout(() => {
              this.changeStateClassById(messageToAnimate.id);
            }, 10);

            if (delay > 0) {
              messageToAnimate.delay = delay;
              messageToAnimate.createdAt = Date.now();
              const effectiveDelay = this.state.hidden ? Math.max(delay * 10, 60000) : delay;
              messageToAnimate.removeTimerId = setTimeout(() => {
                this.removeMessageById(messageToAnimate.id);
              }, effectiveDelay);
            }
          }
        }
      );
    }, 10);

    // If a new message arrived and container was hidden, maybe we should unhide it?
    // User didn't specify, but usually if a new message arrives, we show it, or keep it hidden but increase badge. Let's keep it hidden and badge will increment.

    return newMessage;
  }

  /**
   * Shortcut für {@link show} für Infos
   */
  showInfo(text: string, delay: number, hideCloseButton: boolean) {
    return this.show(text, "info", delay, hideCloseButton);
  }
  /**
   * Shortcut für {@link show} für Warnungen
   */
  showWarning(text: string, delay: number, hideCloseButton: boolean) {
    return this.show(text, "warning", delay, hideCloseButton);
  }
  /**
   * Shortcut für {@link show} für Fehlermeldungen
   */
  showError(text: string, delay: number, hideCloseButton: boolean) {
    return this.show(text, "error", delay, hideCloseButton);
  }

  /**
   * Ändert die Klassen für die Animationen bei Nachricht mit id
   * @param id
   * @param newClass
   */
  changeStateClassById(id: number, newClass?: string) {
    const newMessages = _.clone(this.state.messages);
    const messageToChange = newMessages.find((m) => m.id == id);
    if (messageToChange) {
      messageToChange.stateClass = newClass;
      this.setState({ messages: newMessages });
    }
  }

  /**
   * Entfernt die Nachricht mit id direkt aus der Darstellung (inkl. Animation).
   * Normalerweise aufgerufen bei Klick auf x oder bei Zeit abgelaufen.
   * @param id
   */
  removeMessageById(id: number) {
    this.changeStateClassById(id, "going");
    const message = this.state.messages.find((m) => m.id === id);
    if (message?.onClose) message.onClose();
    setTimeout(() => {
      const oldMessages = this.state.messages;
      const newMessages = oldMessages.filter((m) => m.id !== id);
      this.setState({ messages: newMessages });
    }, 600);
  }
}

/**
 * Klasse um alle Informationen über eine einzelne Nachricht zu bündeln
 */
export class Message {
  messagesObject: Messages;
  id: number;
  text: string;
  count: number;
  type: MessageType;
  hideCloseButton: boolean;
  stateClass: string;
  style: React.CSSProperties;
  onClose: () => void;
  removeTimerId: ReturnType<typeof setTimeout> = null;
  delay: number = 0;
  createdAt: number = Date.now();

  /**
   * Erstellt eine neue Nachricht, die dann eingereiht und verwaltet werden kann.
   * Eigentlich nur für die interne Nutzung vorgesehen.
   * @param messagesObject Parent-Objekt welches die Nachrichten verwaltet (Messages)
   * @param id Eindeutige Nummer
   * @param text Angezeigter Textinhalt
   * @param type Welcher MessageType? Info, Warning, Error
   * @param hideCloseButton Soll der Schließen-Knopf angezeigt werden?
   * @param stateClass CSS-Klasse für die Animationen, interne Verwendung
   * @param style individuelle CSS-Styles für die Nachricht
   * @param onClose Callback, wird beim Timeout oder Close-Klick der Message aufgerufen
   */
  constructor(
    messagesObject: Messages,
    id: number,
    text: string,
    type: MessageType,
    hideCloseButton: boolean,
    stateClass: string,
    style: React.CSSProperties,
    onClose: () => void
  ) {
    this.messagesObject = messagesObject;
    this.id = id;
    this.text = text;
    this.count = 1;
    this.type = type;
    this.hideCloseButton = hideCloseButton;
    this.stateClass = stateClass;
    this.style = style;
    this.onClose = onClose;
  }

  hide() {
    this.messagesObject.removeMessageById(this.id);
  }

  isVisible() {
    return this.messagesObject.state.messages.some((message: Message) => message.id == this.id);
  }

  addCount() {
    this.count++;
  }

  equals(otherMessage: Message) {
    return this.type == otherMessage.type && this.text == otherMessage.text;
  }
}

/**
 * Nachrichten-Kategorien
 */
export type MessageType = "info" | "warning" | "error";
